import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import "./ServicesPage.css";
import { faCheckCircle, faCircleInfo, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FaArrowRight } from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const IMG_STRATEGY =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=80";
const IMG_SEO =
  "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&w=1200&q=80";
const IMG_PAID =
  "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1200&q=80";
const IMG_SOCIAL =
  "https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&w=1200&q=80";
const IMG_EMAIL =
  "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?auto=format&fit=crop&w=1200&q=80";
const IMG_ANALYTICS =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80";

const ServicesPage = () => {
  const { serviceId } = useParams();
  const { webinfo = {} } = useLocalContext();

  const phoneCall = webinfo?.phonecall || webinfo?.phone || "";
  const phoneText = webinfo?.phone || phoneCall || "Contact page";
  const company = webinfo?.name || "smartprintersolutions.com";
  const email = webinfo?.email || "contact@smartprintersolutions.com";

  const services = useMemo(
    () => ({
      "digital-strategy": {
        title: "Digital strategy & consulting",
        kicker: "Goals · Budget · Channels",
        description:
          "Workshops, audits, and roadmaps that align marketing to business outcomes. We document assumptions and risks—we do not promise revenue or market share.",
        details:
          "Engagements may include competitive review, audience research summaries, channel fit analysis, KPI definitions, and phased roadmaps. Strategy outputs are recommendations; execution quality, product-market fit, and external factors determine results.",
        image: IMG_STRATEGY,
        outcomes: [
          "Clear goals and success metrics tied to your funnel",
          "Channel mix options with budget scenarios",
          "Risk register (tracking, compliance, resourcing)",
          "90-day action plan with owners and milestones",
        ],
        focusAreas: [
          { label: "Honesty", text: "We avoid vanity metrics that look good but do not tie to your goals." },
          { label: "Scope", text: "Strategy does not include full execution unless added to the SOW." },
          { label: "Updates", text: "Plans should be refreshed when offers, pricing, or regulation change." },
        ],
        highlights: [
          { icon: faCheckCircle, text: "Executive-ready summaries stakeholders can share internally." },
          { icon: faShieldHalved, text: "Flag regulated claims that may need legal review." },
          { icon: faCircleInfo, text: "Clarify what is in/out of retainer vs. project fees." },
        ],
        sidebar: {
          timeline: "2–6 weeks typical for audits; retainers ongoing",
          deliverables: ["Readout deck", "Roadmap doc", "Optional workshop sessions"],
          bestFor: ["New CMOs", "Founders replatforming", "Teams post-merger"],
        },
      },
      "seo-content": {
        title: "SEO & content marketing",
        kicker: "Technical · Editorial · Measurement",
        description:
          "Improve discoverability with sound technical hygiene, helpful content, and internal linking. Search engines control rankings; we do not guarantee positions or traffic.",
        details:
          "Services may include crawlability fixes, schema where appropriate, content briefs, on-page optimization, and performance reporting. YMYL topics may require expert review—we are not a substitute for qualified professionals in health, finance, or law.",
        image: IMG_SEO,
        outcomes: [
          "Prioritized issue list from technical and content audits",
          "Keyword/topic map aligned to intent",
          "Publishing calendar with briefs writers can execute",
          "Monthly performance narrative with limitations noted",
        ],
        focusAreas: [
          { label: "E-E-A-T", text: "We encourage credible authors and sourcing for sensitive topics." },
          { label: "No guarantees", text: "Algorithm updates can change results without warning." },
          { label: "Collaboration", text: "We need CMS access and subject-matter input for best outcomes." },
        ],
        highlights: [
          { icon: faCheckCircle, text: "Internal linking plans that support discovery and conversion paths." },
          { icon: faShieldHalved, text: "Avoid manipulative tactics that risk manual actions." },
          { icon: faCircleInfo, text: "Document measurement gaps (dark traffic, iOS, ad blockers)." },
        ],
        sidebar: {
          timeline: "Ongoing retainers common; audits as projects",
          deliverables: ["Audit sheets", "Brief templates", "Rank/traffic dashboards"],
          bestFor: ["Sites rebuilding IA", "B2B demand gen", "Local + national brands"],
        },
      },
      "paid-media": {
        title: "Paid search & paid social",
        kicker: "Build · Test · Optimize",
        description:
          "Campaign structure, audience strategy, creative iteration, and budget management across major ad platforms. Performance varies by auction dynamics and creative-market fit.",
        details:
          "We configure conversion tracking, build campaigns, write ad variants within your brand guidelines, and optimize toward agreed KPIs. You remain responsible for offer truthfulness, landing page claims, and industry-specific ad policies.",
        image: IMG_PAID,
        outcomes: [
          "Account structure with naming conventions",
          "Creative testing roadmap",
          "Weekly change logs and rationale",
          "Budget pacing with alerts",
        ],
        focusAreas: [
          { label: "Policy", text: "Ads and destinations must comply with each network’s rules." },
          { label: "Attribution", text: "We explain modeled vs. observed conversions clearly." },
          { label: "Spend risk", text: "Pausing or scaling can affect learning phases and volume." },
        ],
        highlights: [
          { icon: faCheckCircle, text: "Pre-launch QA for pixels, UTMs, and consent tools." },
          { icon: faShieldHalved, text: "Avoid prohibited targeting and sensitive category mistakes." },
          { icon: faCircleInfo, text: "Separate media spend from management fees in reporting." },
        ],
        sidebar: {
          timeline: "Ongoing monthly management typical",
          deliverables: ["Launch checklist", "Experiment backlog", "Monthly review deck"],
          bestFor: ["Lead gen", "E-commerce", "B2B pipeline"],
        },
      },
      "social-media": {
        title: "Social media management",
        kicker: "Brand · Community · Creative",
        description:
          "Editorial calendars, publishing, community moderation playbooks, and coordination with paid social. Voice and compliance stay anchored to your brand standards.",
        details:
          "We plan content types (educational, promotional, cultural), align on approvals, and report engagement metrics with context. Crisis communications require an agreed escalation path and often in-house/legal involvement.",
        image: IMG_SOCIAL,
        outcomes: [
          "Monthly calendar with creative specs",
          "Reply guidelines and escalation matrix",
          "UGC rights reminders where users are featured",
          "Monthly recap with qualitative notes",
        ],
        focusAreas: [
          { label: "Authenticity", text: "Disclose material connections for influencers and partners." },
          { label: "Risk", text: "Real-time news may require pausing scheduled posts." },
          { label: "Platform shifts", text: "Algorithm changes affect reach; plans adapt." },
        ],
        highlights: [
          { icon: faCheckCircle, text: "Creative templates that speed up approvals." },
          { icon: faShieldHalved, text: "Comment moderation aligned to community guidelines." },
          { icon: faCircleInfo, text: "Coordinate boosts with paid team for consistent audiences." },
        ],
        sidebar: {
          timeline: "Monthly retainers; launches may need extra hours",
          deliverables: ["Content calendar", "Asset requests", "Monthly report"],
          bestFor: ["Consumer brands", "SMBs building presence", "B2B thought leadership"],
        },
      },
      "email-marketing": {
        title: "Email & lifecycle marketing",
        kicker: "Lists · Automation · Compliance",
        description:
          "Newsletters, promos, and automated flows with segmentation and deliverability hygiene. We follow common compliance practices (e.g., CAN-SPAM); regulated industries may need specialized counsel.",
        details:
          "We help design journeys (welcome, cart/browse abandon where applicable, win-back), write copy variants, and monitor bounce/complaint rates. List acquisition must be lawful and consented— we do not buy email lists.",
        image: IMG_EMAIL,
        outcomes: [
          "List health audit and sunset rules",
          "Flow maps with triggers and goals",
          "Template system aligned to brand",
          "Monthly performance with cohort notes",
        ],
        focusAreas: [
          { label: "Consent", text: "Honors opt-in/opt-out and preference centers." },
          { label: "Deliverability", text: "Auth (SPF/DKIM/DMARC) coordinated with your IT." },
          { label: "Content", text: "Subject lines must not mislead about offer or sender." },
        ],
        highlights: [
          { icon: faCheckCircle, text: "A/B tests on subject, CTA, and layout where volume allows." },
          { icon: faShieldHalved, text: "Avoid sensitive data in unsecured email content." },
          { icon: faCircleInfo, text: "Coordinate SMS separately—different rules apply." },
        ],
        sidebar: {
          timeline: "Flows: 3–6 weeks; newsletters ongoing",
          deliverables: ["Journey diagrams", "Copy docs", "QA checklist"],
          bestFor: ["E-commerce", "SaaS trials", "Event promotion"],
        },
      },
      "analytics-cro": {
        title: "Analytics & conversion optimization",
        kicker: "Tags · Dashboards · Tests",
        description:
          "GA4/GTM implementations, event governance, dashboards, and structured CRO tests on landing pages. Uplift is not guaranteed; tests need traffic and sound hypotheses.",
        details:
          "We map events to business questions, fix broken tracking, and propose experiments with clear success criteria. Statistical significance and seasonality matter—we avoid declaring winners prematurely.",
        image: IMG_ANALYTICS,
        outcomes: [
          "Measurement plan with event dictionary",
          "Debugged implementation notes",
          "Executive dashboard or Looker/Sheets views",
          "Test hypotheses backlog prioritized by impact/effort",
        ],
        focusAreas: [
          { label: "Privacy", text: "Respect consent mode and regional requirements you adopt." },
          { label: "Data quality", text: "Garbage in, garbage out—audits first when needed." },
          { label: "Sample size", text: "Low traffic limits how fast you can learn." },
        ],
        highlights: [
          { icon: faCheckCircle, text: "QA events against real user flows before trusting dashboards." },
          { icon: faShieldHalved, text: "Document admin changes to prevent silent breakage." },
          { icon: faCircleInfo, text: "Pair quant drops with qual tools when useful." },
        ],
        sidebar: {
          timeline: "Audits: a few weeks; testing ongoing",
          deliverables: ["Tag spec", "Dashboard build", "Test readouts"],
          bestFor: ["Teams flying blind", "Post-migration GA4", "Landing page programs"],
        },
      },
    }),
    []
  );

  const service = services[serviceId];
  if (!service) {
    return (
      <div className="sp sp--missing">
        <p>We could not find that service overview.</p>
        <Link className="sp__back" to="/services">
          ← Back to all services
        </Link>
      </div>
    );
  }

  return (
    <div className="sp">
      <motion.section className="sp__hero" initial="hidden" animate="show" variants={fadeUp}>
        <div className="sp__heroGrid">
          <div>
            <div className="sp__crumb">
              <span className="sp__crumbDot" aria-hidden="true" />
              {service.kicker}
            </div>

            <h1 className="sp__heroTitle">{service.title}</h1>

            <p className="sp__heroSub">{service.description}</p>

            <div className="sp__heroActions">
              <Link className="sp__btn sp__btn--primary" to="/contact">
                Request a proposal <FaArrowRight aria-hidden />
              </Link>

              {phoneCall ? (
                <a className="sp__btn sp__btn--ghost" href={`tel:${phoneCall}`}>
                  Call {phoneText}
                </a>
              ) : (
                <a className="sp__btn sp__btn--ghost" href={`mailto:${email}`}>
                  Email {email}
                </a>
              )}
            </div>
          </div>

          <div className="sp__heroCard">
            <div className="sp__heroCardTop">
              <div>
                <p className="sp__heroCardK">Overview</p>
                <h2 className="sp__heroCardT">What to expect</h2>
              </div>
              <span className="sp__heroChip">Custom scope</span>
            </div>

            <div className="sp__heroImg">
              <img src={service.image} alt="" />
            </div>

            <ul className="sp__heroList">
              {service.outcomes.slice(0, 4).map((o, idx) => (
                <li key={idx}>
                  <span className="sp__tick">✓</span>
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.section>

      <div className="sp__wrap">
        <motion.main
          className="sp__main"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={fadeUp}
        >
          <section className="sp__sec">
            <h2 className="sp__h2">Details</h2>
            <p className="sp__p">{service.details}</p>
          </section>

          <section className="sp__sec">
            <h2 className="sp__h2">Typical outputs</h2>
            <ul className="sp__features">
              {service.outcomes.map((f, i) => (
                <li key={i}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="sp__sec">
            <h2 className="sp__h2">Limits &amp; guardrails</h2>
            <div className="sp__focus">
              {service.focusAreas.map((g, i) => (
                <div className="sp__focusCard" key={i}>
                  <h3 className="sp__focusTitle">{g.label}</h3>
                  <p className="sp__focusText">{g.text}</p>
                </div>
              ))}
            </div>

            <ul className="sp__features" style={{ marginTop: "1.4rem" }}>
              {service.highlights.map((h, i) => (
                <li key={i}>
                  <FontAwesomeIcon icon={h.icon} />
                  <span>{h.text}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="sp__sec sp__sec--notice">
            <h2 className="sp__h2">Contracts beat marketing copy</h2>
            <p className="sp__p">
              This page summarizes how we talk about the service. Fees, timelines, and deliverables are confirmed in a
              signed agreement. See also our <Link to="/disclaimer">Disclaimer</Link> and{" "}
              <Link to="/Terms-and-condition">Terms</Link>.
            </p>
          </section>
        </motion.main>

        <aside className="sp__side">
          <div className="sp__box">
            <h3>Best for</h3>
            <p>Teams that want experienced operators and transparent reporting—not guaranteed outcomes.</p>
            <ul className="sp__miniList">
              {service.sidebar.bestFor.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>

          <div className="sp__box">
            <h3>Common deliverables</h3>
            <p>Exact lists are tailored in your proposal.</p>
            <ul className="sp__miniList">
              {service.sidebar.deliverables.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>

          <div className="sp__box">
            <h3>Talk to {company}</h3>
            <p>Share goals, budget, and timeline. We will confirm fit before sending a formal scope.</p>
            <div style={{ marginTop: "1.2rem", display: "grid", gap: "0.9rem" }}>
              <Link className="sp__btn sp__btn--primary" to="/contact">
                Contact <FaArrowRight />
              </Link>
              {phoneCall ? (
                <a className="sp__btn sp__btn--ghost" href={`tel:${phoneCall}`}>
                  Call {phoneText}
                </a>
              ) : (
                <a className="sp__btn sp__btn--ghost" href={`mailto:${email}`}>
                  Email us
                </a>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ServicesPage;
